import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { ResponsibilityMatrix } from '@/types';

interface ResponsibilitiesTableProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

const EMPTY_FORM = {
  component: '',
  responsible_team: '',
  contact_space_url: '',
  escalation_email: '',
  keywords: [] as string[],
};

export default function ResponsibilitiesTable({ teamId, onNotify }: ResponsibilitiesTableProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ResponsibilityMatrix | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newKeyword, setNewKeyword] = useState('');

  const { data: responsibilities } = useQuery({
    queryKey: ['team-responsibilities', teamId],
    queryFn: () => teamApi.getResponsibilities(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const addMutation = useMutation({
    mutationFn: (data: Partial<ResponsibilityMatrix>) =>
      teamApi.addResponsibility(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-responsibilities', teamId] });
      onNotify('Responsibility added successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to add responsibility: ${getErrorMessage(error)}`, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ResponsibilityMatrix> }) =>
      teamApi.updateResponsibility(teamId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-responsibilities', teamId] });
      onNotify('Responsibility updated successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to update responsibility: ${getErrorMessage(error)}`, 'error');
    },
  });

  const removeRespMutation = useMutation({
    mutationFn: (respId: string) => teamApi.removeResponsibility(teamId, respId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-responsibilities', teamId] });
      onNotify('Responsibility removed successfully!', 'success');
    },
    onError: (error: Error) => {
      onNotify(`Failed to remove responsibility: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleOpen = (item?: ResponsibilityMatrix) => {
    if (item) {
      setEditingItem(item);
      setForm({
        component: item.component,
        responsible_team: item.responsible_team,
        contact_space_url: item.contact_space_url || '',
        escalation_email: item.escalation_email || '',
        keywords: item.keywords || [],
      });
    } else {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    }
    setNewKeyword('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setNewKeyword('');
  };

  const handleSave = () => {
    const data: Partial<ResponsibilityMatrix> = {
      component: form.component,
      responsible_team: form.responsible_team,
      contact_space_url: form.contact_space_url || undefined,
      escalation_email: form.escalation_email || undefined,
      keywords: form.keywords,
    };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleRemove = (respId: string) => {
    if (window.confirm('Are you sure you want to remove this responsibility?')) {
      removeRespMutation.mutate(respId);
    }
  };

  const handleAddKeyword = () => {
    const kw = newKeyword.trim();
    if (kw && !form.keywords.includes(kw)) {
      setForm((prev) => ({ ...prev, keywords: [...prev.keywords, kw] }));
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((k) => k !== keyword) }));
  };

  const isSaving = addMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Responsibility
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              <TableCell>Responsible Team</TableCell>
              <TableCell>Keywords</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {responsibilities?.map((resp) => (
              <TableRow key={resp.id}>
                <TableCell>{resp.component}</TableCell>
                <TableCell>{resp.responsible_team}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {resp.keywords.slice(0, 3).map((kw, i) => (
                      <Chip key={i} label={kw} size="small" variant="outlined" />
                    ))}
                    {resp.keywords.length > 3 && (
                      <Chip label={`+${resp.keywords.length - 3}`} size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {resp.contact_space_url ? (
                    <a href={resp.contact_space_url} target="_blank" rel="noopener noreferrer">
                      Contact Space
                    </a>
                  ) : resp.escalation_email || '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit responsibility">
                    <IconButton size="small" onClick={() => handleOpen(resp)} sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove responsibility">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(resp.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {responsibilities?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No teams matrix entries yet. Add other teams and their components/keywords to help the bot redirect out-of-scope queries.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Responsibility' : 'Add Responsibility'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Component"
              value={form.component}
              onChange={(e) => setForm((prev) => ({ ...prev, component: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Responsible Team"
              value={form.responsible_team}
              onChange={(e) => setForm((prev) => ({ ...prev, responsible_team: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Contact Space URL"
              value={form.contact_space_url}
              onChange={(e) => setForm((prev) => ({ ...prev, contact_space_url: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Escalation Email"
              type="email"
              value={form.escalation_email}
              onChange={(e) => setForm((prev) => ({ ...prev, escalation_email: e.target.value }))}
              fullWidth
            />
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label="Add Keyword"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddKeyword();
                    }
                  }}
                  size="small"
                  fullWidth
                />
                <Button variant="outlined" onClick={handleAddKeyword} disabled={!newKeyword.trim()}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {form.keywords.map((kw, i) => (
                  <Chip key={i} label={kw} size="small" onDelete={() => handleRemoveKeyword(kw)} />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              isSaving ||
              !form.component.trim() ||
              !form.responsible_team.trim() ||
              form.keywords.length === 0
            }
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
